import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { talkvitApi } from "../../api/talkvitAPI";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IGenAiPrompt, IGenAiContext } from "./genAi.types";

// ============ GenAI Prompt Actions ============

export const FETCH_GENAI_PROMPT_LIST_PROGRESS =
  "FETCH_GENAI_PROMPT_LIST_PROGRESS";
export const FETCH_GENAI_PROMPT_LIST_SUCCESS =
  "FETCH_GENAI_PROMPT_LIST_SUCCESS";
export const FETCH_GENAI_PROMPT_LIST_FAILED = "FETCH_GENAI_PROMPT_LIST_FAILED";
export const FETCH_GENAI_PROMPT_PROGRESS = "FETCH_GENAI_PROMPT_PROGRESS";
export const FETCH_GENAI_PROMPT_SUCCESS = "FETCH_GENAI_PROMPT_SUCCESS";
export const FETCH_GENAI_PROMPT_FAILED = "FETCH_GENAI_PROMPT_FAILED";
export const CLEAR_GENAI_PROMPT = "CLEAR_GENAI_PROMPT";
export const CLEAR_GENAI_PROMPT_STATE = "CLEAR_GENAI_PROMPT_STATE";

export const fetchGenAiPromptListProgress = () =>
  action(FETCH_GENAI_PROMPT_LIST_PROGRESS);
export const fetchGenAiPromptListSuccess = (
  data: IGenAiPrompt[],
  totalRecords: number,
  currentRecords: number
) => action(FETCH_GENAI_PROMPT_LIST_SUCCESS, { data, totalRecords, currentRecords });
export const fetchGenAiPromptListFailed = () =>
  action(FETCH_GENAI_PROMPT_LIST_FAILED);
export const fetchGenAiPromptProgress = () =>
  action(FETCH_GENAI_PROMPT_PROGRESS);
export const fetchGenAiPromptSuccess = (data: IGenAiPrompt) =>
  action(FETCH_GENAI_PROMPT_SUCCESS, { data });
export const fetchGenAiPromptFailed = (errorMessage: string) =>
  action(FETCH_GENAI_PROMPT_FAILED, { errorMessage });
export const clearGenAiPrompt = () => action(CLEAR_GENAI_PROMPT);
export const clearGenAiPromptState = () => action(CLEAR_GENAI_PROMPT_STATE);

// Async: Fetch prompt list
export const fetchGenAiPromptListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    date: any,
    searchValue: any[],
    company_outlet_uuid?: string,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchGenAiPromptListProgress());
      dispatch(saveLoaderProgress());
      let url = `/genai/get-genai-prompt?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}`;
      if (company_outlet_uuid)
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      if (searchValue.length > 0)
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      if (status) url += `&status=${status}`;
      const response = await talkvitApi.get(url);
      if (response.status === 200 && response.data) {
        dispatch(
          fetchGenAiPromptListSuccess(
            response.data.data,
            response.data.totalRecords,
            response.data.currentRecords
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchGenAiPromptListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch GenAI prompts",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Async: Fetch single prompt
export const fetchGenAiPromptAsync =
  (promptId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchGenAiPromptProgress());
      dispatch(saveLoaderProgress());
      const res = await talkvitApi.get(
        `/genai/get-genai-prompt?pageNo=1&itemPerPage=10&genai_prompt_uuid=${promptId}`
      );
      const data: IGenAiPrompt[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchGenAiPromptSuccess(data[0]));
      } else {
        dispatch(fetchGenAiPromptFailed("No record found"));
      }
    } catch (err: any) {
      dispatch(fetchGenAiPromptFailed(""));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch prompt",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Async: Upsert prompt
export const upsertGenAiPromptAsync =
  (
    data: IGenAiPrompt,
    onCallBack: (isSuccess?: boolean, data?: IGenAiPrompt) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const res = await talkvitApi.post("/genai/upsert-genai-prompt", data);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      onCallBack(true, res.data.data);
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "An error occurred",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ============ GenAI Context Actions ============

export const FETCH_GENAI_CONTEXT_LIST_PROGRESS =
  "FETCH_GENAI_CONTEXT_LIST_PROGRESS";
export const FETCH_GENAI_CONTEXT_LIST_SUCCESS =
  "FETCH_GENAI_CONTEXT_LIST_SUCCESS";
export const FETCH_GENAI_CONTEXT_LIST_FAILED =
  "FETCH_GENAI_CONTEXT_LIST_FAILED";
export const FETCH_GENAI_CONTEXT_PROGRESS = "FETCH_GENAI_CONTEXT_PROGRESS";
export const FETCH_GENAI_CONTEXT_SUCCESS = "FETCH_GENAI_CONTEXT_SUCCESS";
export const FETCH_GENAI_CONTEXT_FAILED = "FETCH_GENAI_CONTEXT_FAILED";
export const CLEAR_GENAI_CONTEXT = "CLEAR_GENAI_CONTEXT";
export const CLEAR_GENAI_CONTEXT_STATE = "CLEAR_GENAI_CONTEXT_STATE";

export const fetchGenAiContextListProgress = () =>
  action(FETCH_GENAI_CONTEXT_LIST_PROGRESS);
export const fetchGenAiContextListSuccess = (
  data: IGenAiContext[],
  totalRecords: number,
  currentRecords: number
) => action(FETCH_GENAI_CONTEXT_LIST_SUCCESS, { data, totalRecords, currentRecords });
export const fetchGenAiContextListFailed = () =>
  action(FETCH_GENAI_CONTEXT_LIST_FAILED);
export const fetchGenAiContextProgress = () =>
  action(FETCH_GENAI_CONTEXT_PROGRESS);
export const fetchGenAiContextSuccess = (data: IGenAiContext) =>
  action(FETCH_GENAI_CONTEXT_SUCCESS, { data });
export const fetchGenAiContextFailed = (errorMessage: string) =>
  action(FETCH_GENAI_CONTEXT_FAILED, { errorMessage });
export const clearGenAiContext = () => action(CLEAR_GENAI_CONTEXT);
export const clearGenAiContextState = () => action(CLEAR_GENAI_CONTEXT_STATE);

// Async: Fetch context list
export const fetchGenAiContextListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    searchValue?: any[] | null,
    company_outlet_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchGenAiContextListProgress());
      dispatch(saveLoaderProgress());
      let url = `/genai/get-genai-context?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&status=ACTIVE`;
      if (company_outlet_uuid)
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      if (searchValue && searchValue.length > 0)
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      const response = await talkvitApi.get(url);
      if (response.status === 200 && response.data) {
        dispatch(
          fetchGenAiContextListSuccess(
            response.data.data,
            response.data.totalRecords,
            response.data.currentRecords
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchGenAiContextListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch GenAI contexts",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Async: Fetch single context
export const fetchGenAiContextAsync =
  (contextId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchGenAiContextProgress());
      dispatch(saveLoaderProgress());
      const res = await talkvitApi.get(
        `/genai/get-genai-context?pageNo=1&itemPerPage=10&genai_context_uuid=${contextId}`
      );
      const data: IGenAiContext[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchGenAiContextSuccess(data[0]));
      } else {
        dispatch(fetchGenAiContextFailed("No record found"));
      }
    } catch (err: any) {
      dispatch(fetchGenAiContextFailed(""));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch context",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Async: Upsert context
export const upsertGenAiContextAsync =
  (
    data: IGenAiContext,
    onCallBack: (isSuccess?: boolean, data?: IGenAiContext) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const { create_ts, insert_ts, ...rest } = data;
      const res = await talkvitApi.post("/genai/upsert-genai-context", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      onCallBack(true, res.data.data);
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "An error occurred",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
